'use client';

import { useEffect, useState, createContext, useContext } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  Menu,
  FolderOpen,
  Map,
  ClipboardList,
  Users,
  User,
  HelpCircle,
  LogOut,
  ChevronLeft,
  Link2,
  ScrollText,
  Settings,
  Plus,
  CalendarDays,
  MessageCircle,
  Download,
  MonitorSmartphone,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAuth } from '@/providers/auth-provider';
import { CHAT_ACTIVITY_EVENT, useChatStream } from '@/providers/chat-stream-provider';
import { staffRoleLabel } from '@/lib/staff-role-label';
import { cn } from '@/lib/utils';
import { AppVersion } from '@/components/layout/app-version';
import { listChatConversations } from '@/lib/api/chat';

// Контекст для передачи состояния сворачивания в layout
interface SidebarContextType {
  isCollapsed: boolean;
  setIsCollapsed: (value: boolean) => void;
}

const SidebarContext = createContext<SidebarContextType>({
  isCollapsed: false,
  setIsCollapsed: () => {},
});

export const useSidebar = () => useContext(SidebarContext);

interface NavItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  adminOnly?: boolean;
  plusHref?: string;
  plusAriaLabel?: string;
}

const navItems: NavItem[] = [
  {
    title: 'Подключение',
    href: '/',
    icon: FolderOpen,
    plusHref: '/requests/create',
    plusAriaLabel: 'Создать заявку на подключение',
  },
  {
    title: 'Сервис',
    href: '/service-requests',
    icon: ClipboardList,
    plusHref: '/service-requests/create',
    plusAriaLabel: 'Создать заявку на сервис',
  },
  { title: 'Чаты', href: '/chat', icon: MessageCircle },
  { title: 'Приложения', href: '/apps', icon: MonitorSmartphone },
  { title: 'Календарь', href: '/calendar', icon: CalendarDays },
  { title: 'Карта', href: '/maps', icon: Map },
  { title: 'Абоненты', href: '/subscribers', icon: User, adminOnly: true },
  { title: 'Пользователи', href: '/users', icon: Users, adminOnly: true },
  { title: 'Интеграции Felix', href: '/integrations/felix', icon: Link2, adminOnly: true },
  { title: 'Журнал действий', href: '/audit-logs', icon: ScrollText, adminOnly: true },
  { title: 'Настройки', href: '/settings', icon: Settings, adminOnly: true },
  { title: 'Помощь', href: '/help', icon: HelpCircle },
];

const TRAY_DOWNLOAD_DEB =
  process.env.NEXT_PUBLIC_GIGLAN_TRAY_DOWNLOAD_DEB ?? '/downloads/giglan-tray_1.0.1_amd64.deb';
const TRAY_DOWNLOAD_WINDOWS = process.env.NEXT_PUBLIC_GIGLAN_TRAY_DOWNLOAD_WINDOWS ?? '/downloads/giglan-tray_1.0.1_win64.zip';

interface SidebarContentProps {
  isMobile?: boolean;
  onClose?: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

function SidebarContent({ isMobile = false, onClose, isCollapsed = false, onToggleCollapse }: SidebarContentProps) {
  const pathname = usePathname();
  const { user, logout } = useAuth();
  const { connection: chatStreamConnection } = useChatStream();
  const isAdmin = user?.role === 'admin';
  const [chatUnread, setChatUnread] = useState(0);

  useEffect(() => {
    const baseTitle = 'Giglan safe';
    document.title = chatUnread > 0 ? `(${chatUnread}) ${baseTitle}` : baseTitle;
    return () => {
      document.title = baseTitle;
    };
  }, [chatUnread]);

  useEffect(() => {
    if (!user) return;
    const loadUnread = () => {
      listChatConversations({ status: 'open', limit: 100 })
        .then((data) => {
          const count = (data.conversations ?? []).reduce((sum, item) => sum + (item.unread_count || 0), 0);
          setChatUnread(count);
        })
        .catch(() => undefined);
    };
    loadUnread();
    const timer = window.setInterval(loadUnread, 10000);
    const onActivity = () => loadUnread();
    window.addEventListener(CHAT_ACTIVITY_EVENT, onActivity as EventListener);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener(CHAT_ACTIVITY_EVENT, onActivity as EventListener);
    };
  }, [user]);

  const filteredItems = navItems.filter(
    (item) => !item.adminOnly || (item.adminOnly && isAdmin)
  );

  const requestBrowserNotifications = () => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      void Notification.requestPermission();
    }
  };

  const downloadLinks = [
    { label: 'Скачать .deb', href: TRAY_DOWNLOAD_DEB },
    { label: 'Windows', href: TRAY_DOWNLOAD_WINDOWS },
  ].filter((item) => item.href);

  // На mobile без SheetClose вокруг строки — иначе возможны предупреждения гидрации; закрытие sheet по onClick на Link.
  const NavLink = ({
    item,
    isActive,
  }: {
    item: NavItem;
    isActive: boolean;
  }) => {
    const Icon = item.icon;
    const showStreamDot = item.href === '/chat';
    const iconEl = showStreamDot ? (
      <span className="relative inline-flex shrink-0">
        <Icon className={cn('h-6 w-6', isActive ? 'text-primary' : 'text-foreground')} />
        <span
          className={cn(
            'absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full border-2 border-[#EAECF1]',
            chatStreamConnection === 'connected' && 'bg-emerald-500',
            chatStreamConnection === 'connecting' && 'animate-pulse bg-amber-400',
            chatStreamConnection === 'disconnected' && 'bg-red-500'
          )}
          title={
            chatStreamConnection === 'connected'
              ? 'Поток чата подключён'
              : chatStreamConnection === 'connecting'
                ? 'Подключение…'
                : 'Нет потока'
          }
        />
      </span>
    ) : (
      <Icon className={cn('h-6 w-6 shrink-0', isActive ? 'text-primary' : 'text-foreground')} />
    );
    const listLink = (
      <Link
        href={item.href}
        onClick={isMobile ? () => onClose?.() : undefined}
        className={cn(
          'flex min-w-0 flex-1 items-center text-lg font-normal leading-snug transition-colors',
          isCollapsed ? 'min-w-0 flex-none justify-center gap-0' : 'gap-2.5',
          isActive ? 'text-primary' : 'text-foreground'
        )}
      >
        {iconEl}
        {!isCollapsed ? (
          <span className="min-w-0 flex-1 truncate">{item.title}</span>
        ) : null}
        {!isCollapsed && item.href === '/chat' && chatUnread > 0 ? (
          <Badge className="ml-auto bg-[#F46922] text-white">{chatUnread}</Badge>
        ) : null}
      </Link>
    );

    const plusLabel = item.plusAriaLabel || 'Создать';
    const plusLink =
      item.plusHref && !isCollapsed ? (
        <Link
          href={item.plusHref}
          onClick={isMobile ? () => onClose?.() : undefined}
          aria-label={plusLabel}
          title={plusLabel}
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground transition-colors hover:bg-primary/90 hover:text-primary-foreground"
        >
          <Plus className="h-3.5 w-3.5" />
        </Link>
      ) : null;

    const row = (
      <div
        className={cn(
          'flex w-full min-w-0 flex-nowrap items-center gap-x-2',
          isCollapsed && 'justify-center'
        )}
      >
        {listLink}
        {plusLink}
      </div>
    );

    if (isMobile) {
      return row;
    }

    if (isCollapsed) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            {listLink}
          </TooltipTrigger>
          <TooltipContent side="right">
            <p>{item.title}</p>
            {item.href === '/chat' ? (
              <p className="mt-1 max-w-[220px] text-xs text-muted-foreground">
                {chatStreamConnection === 'connected'
                  ? 'Поток чата подключён'
                  : chatStreamConnection === 'connecting'
                    ? 'Подключение к потоку…'
                    : 'Поток отключён, данные обновляются по расписанию'}
              </p>
            ) : null}
          </TooltipContent>
        </Tooltip>
      );
    }

    return row;
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    if (isCollapsed && onToggleCollapse) {
      e.preventDefault();
      onToggleCollapse();
    }
  };

  return (
    <div className="relative flex h-full flex-col overflow-hidden">
      {/* Header - логотип и кнопка сворачивания */}
      <div className={cn(
        "border-b border-[#D8DFEA] py-[10px] flex items-center",
        isCollapsed ? "justify-center px-0" : "justify-between px-[30px]"
      )}>
        {isCollapsed ? (
          <button
            onClick={handleLogoClick}
            className="flex items-center justify-center"
            aria-label="Развернуть меню"
          >
            <img 
              src="/icons/favicon.svg" 
              alt="Giglan" 
              className="h-[22px] w-[22px]"
            />
          </button>
        ) : (
          <>
            <Link href="/" className="flex items-center">
              <img src="/logo.svg" alt="Giglan" className="h-[22px] w-auto" />
            </Link>
            {onToggleCollapse && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onToggleCollapse}
                className="h-8 w-8 text-[#68707D] hover:text-[#4F5762] hover:bg-[#F5F5F5]"
                aria-label="Свернуть меню"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            )}
          </>
        )}
      </div>

      {/* Блок пользователя - только в развернутом состоянии */}
      {!isCollapsed && user && (
        <div className="border-b border-[#D8DFEA] px-[30px] py-[15px]">
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#1A1A1A] truncate">
                {user.full_name || user.email}
              </p>
              <Badge
                variant={user.role === 'admin' ? 'default' : 'secondary'}
                className={cn(
                  "mt-1",
                  user.role === 'admin'
                    ? 'bg-[#F46922] text-white border-transparent'
                    : 'bg-[#F5F5F5] text-[#4F5762] border-transparent'
                )}
              >
                {staffRoleLabel(user.role)}
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={async () => {
                if (!confirm('Вы уверены, что хотите выйти?')) {
                  return;
                }
                if (isMobile && onClose) {
                  onClose();
                }
                await logout();
              }}
              className="h-8 w-8 text-[#68707D] hover:text-[#4F5762] hover:bg-[#F5F5F5] shrink-0"
              aria-label="Выйти"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Навигация */}
      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col',
          isCollapsed ? 'p-4' : 'px-[30px] pb-[30px] pt-0'
        )}
      >
        <nav
          className={cn(
            'min-h-0 flex-1 space-y-6 overflow-y-auto',
            isCollapsed ? 'pt-2' : 'pt-[38px]'
          )}
        >
          {filteredItems.map((item) => {
            const isActive = pathname === item.href;
            return (
              <NavLink key={item.href} item={item} isActive={isActive} />
            );
          })}
        </nav>

        {downloadLinks.length > 0 ? (
          <div className={cn('border-t border-[#D8DFEA]', isCollapsed ? 'pt-3' : 'pt-4')}>
            {isCollapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <a
                    href={downloadLinks[0].href}
                    download
                    onClick={requestBrowserNotifications}
                    className="mx-auto flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground transition-colors hover:bg-primary/90"
                    aria-label={downloadLinks[0].label}
                    title={downloadLinks[0].label}
                  >
                    <Download className="h-4 w-4" />
                  </a>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>{downloadLinks[0].label}</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <div className="space-y-2">
                {downloadLinks.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    download
                    onClick={requestBrowserNotifications}
                    className="flex h-9 w-full items-center justify-center gap-2 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                  >
                    <Download className="h-4 w-4" />
                    {item.label}
                  </a>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Version info - только в развернутом состоянии */}
      {!isCollapsed && (
        <div className="border-t border-[#D8DFEA] px-[30px] py-[15px]">
          <AppVersion />
        </div>
      )}
    </div>
  );
}

export function AppSidebar() {
  const [open, setOpen] = useState(false);
  const { isCollapsed, setIsCollapsed } = useSidebar();
  
  const toggleCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
  };

  return (
    <>
      {/* Desktop Sidebar - fixed left */}
      <aside className={cn(
        "hidden md:flex md:flex-col fixed inset-y-0 left-0 z-40 bg-[#EAECF1] overflow-hidden transition-all duration-300",
        isCollapsed ? 'md:w-16' : 'md:w-64'
      )}>
        <TooltipProvider>
          <SidebarContent
            isMobile={false}
            onClose={undefined}
            isCollapsed={isCollapsed}
            onToggleCollapse={toggleCollapse}
          />
        </TooltipProvider>
      </aside>

      {/* Mobile Header */}
      <header className="sticky top-0 z-50 flex h-14 items-center border-b border-[#D8DFEA] bg-white px-4 md:hidden">
        {/* Menu button - LEFT */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-10 w-10 text-[#68707D] hover:text-[#4F5762] hover:bg-[#F5F5F5]"
            >
              <Menu className="h-5 w-5" />
              <span className="sr-only">Menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[280px] p-0 bg-[#EAECF1] border-r border-[#D8DFEA]">
            <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
            <TooltipProvider>
              <SidebarContent
                isMobile={true}
                onClose={() => setOpen(false)}
                isCollapsed={false}
              />
            </TooltipProvider>
          </SheetContent>
        </Sheet>

        {/* Logo - CENTER */}
        <div className="flex-1 flex justify-center">
          <Link href="/">
            <img src="/logo.svg" alt="Giglan" className="h-6 w-auto" />
          </Link>
        </div>

        {/* Spacer for balance */}
        <div className="w-10" />
      </header>
    </>
  );
}

// Компонент-обертка для layout с провайдером контекста
export function SidebarLayout({ children }: { children: React.ReactNode }) {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('sidebar-collapsed') === 'true';
    }
    return false;
  });

  const handleSetCollapsed = (value: boolean) => {
    setIsCollapsed(value);
    if (typeof window !== 'undefined') {
      localStorage.setItem('sidebar-collapsed', String(value));
    }
  };

  return (
    <SidebarContext.Provider value={{ isCollapsed, setIsCollapsed: handleSetCollapsed }}>
      {children}
    </SidebarContext.Provider>
  );
}
